import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Calendar, Clock, Users, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { ConsentToggleRow } from "../components/ConsentToggleRow";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "../components/ui/sonner";
import { formatDate, formatTime } from "../lib/dateUtils";
import { WaiverModal } from "../components/WaiverModal";

const TYPE_LABEL = { romantic: "Romantic Stranger Session", friendship: "Friendship Stranger Session", creative: "Creative Stranger Session" };

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);
  const [consent, setConsent] = useState({ hand_holding: false, hugging: false, sitting_close: false, arm_around_shoulder: false, other: false });
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    api
      .get(`/sessions/${id}`)
      .then(({ data }) => setSession(data))
      .catch(() => setSession(false));
  }, [id]);

  const startBooking = () => {
    if (!user) return navigate("/signup?role=stranger");
    if (user.role !== "stranger") return toast.error("Only Stranger accounts can book sessions.");
    if (!user.profile_completed) return navigate("/register");
    setShowConsent(true);
  };

  const confirmBooking = async () => {
    setBooking(true);
    try {
      const { data } = await api.post("/bookings", { session_id: id, consent });
      toast.success("You're registered! We'll let you know once you're matched.");
      setShowConsent(false);
      navigate(`/dashboard/bookings/${data.id}`);
    } catch (e) {
      if (e.response?.status === 412 && e.response?.data?.detail === "waiver_required") {
        setShowWaiver(true);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally {
      setBooking(false);
    }
  };

  if (session === null) return <div className="py-32 text-center font-medium" data-testid="session-loading">Loading session...</div>;
  if (session === false) return <div className="py-32 text-center font-medium" data-testid="session-not-found">Session not found.</div>;

  return (
    <div data-testid="session-detail-page">
      <div className="relative h-[55vh] min-h-[420px]">
        <img src={session.image_url} alt={session.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight-black via-midnight-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 p-6 sm:p-10 max-w-4xl">
          <span className="bg-stranger-orange text-clean-white text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full">{TYPE_LABEL[session.session_type]}</span>
          <h1 className="text-clean-white font-extrabold uppercase text-3xl sm:text-5xl tracking-tight mt-4" data-testid="session-title">
            {session.title}
          </h1>
          <p className="text-clean-white/80 font-medium mt-2 flex items-center gap-2">
            Hosted by{" "}
            <Link to={`/photographers/${session.photographer?.slug || session.photographer?.id}`} data-testid="session-photographer-link" className="text-clean-white hover:text-stranger-orange underline transition-colors">
              {session.photographer?.business_name}
            </Link>{" "}
            {session.photographer?.verified && <VerifiedBadge compact />}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-10">
          <div>
            <h2 className="font-extrabold uppercase text-lg mb-3">About This Session</h2>
            <p className="text-midnight-black/80 font-medium leading-relaxed" data-testid="session-description">
              {session.description}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <h3 className="font-extrabold uppercase text-sm mb-2 text-stranger-orange">What to Expect</h3>
              <p className="text-sm font-medium text-midnight-black/80">{session.what_to_expect}</p>
            </div>
            <div>
              <h3 className="font-extrabold uppercase text-sm mb-2 text-stranger-orange">What to Wear</h3>
              <p className="text-sm font-medium text-midnight-black/80">{session.what_to_wear}</p>
            </div>
            <div>
              <h3 className="font-extrabold uppercase text-sm mb-2 text-stranger-orange">Duration</h3>
              <p className="text-sm font-medium text-midnight-black/80">Approximately {session.duration_minutes} minutes</p>
            </div>
            <div>
              <h3 className="font-extrabold uppercase text-sm mb-2 text-stranger-orange">What's Included</h3>
              <ul className="text-sm font-medium text-midnight-black/80 space-y-1 list-disc list-inside">
                {session.whats_included?.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          </div>
          <div>
            <h3 className="font-extrabold uppercase text-sm mb-2 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-stranger-orange" /> Safety Information
            </h3>
            <p className="text-sm font-medium text-midnight-black/80">{session.safety_info}</p>
          </div>
          <div>
            <h3 className="font-extrabold uppercase text-sm mb-2">Cancellation Policy</h3>
            <p className="text-sm font-medium text-midnight-black/80">{session.cancellation_policy}</p>
          </div>
        </div>

        <div className="bg-warm-white border border-soft-gray rounded-2xl p-6 h-fit md:sticky md:top-28" data-testid="session-booking-panel">
          <p className="text-3xl font-extrabold mb-1">
            ${session.price}
            <span className="text-sm font-medium text-midnight-black/60"> / Stranger</span>
          </p>
          <div className="space-y-2 text-sm font-medium text-midnight-black/80 mt-4 mb-6">
            <p className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-stranger-orange" /> {session.address}, {session.city}, {session.state}
            </p>
            <p className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-stranger-orange" /> {formatDate(session.session_date)}
            </p>
            <p className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-stranger-orange" /> {formatTime(session.session_time)}
            </p>
            <p className="flex items-center gap-2">
              <Users className="w-4 h-4 text-stranger-orange" /> {session.spots_remaining} of {session.capacity} spots left
            </p>
          </div>
          <Button
            data-testid="book-session-button"
            onClick={startBooking}
            disabled={session.spots_remaining <= 0}
            className="w-full bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold tracking-wide rounded-full py-6"
          >
            {session.spots_remaining <= 0 ? "FULLY BOOKED" : "I WANT TO BE A STRANGER"}
          </Button>
        </div>
      </div>

      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent data-testid="consent-dialog">
          <DialogHeader>
            <DialogTitle>Your Comfort, Your Choice</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-midnight-black/70 font-medium mb-2">
            Nothing here is assumed. Choose what you're comfortable with for this session — you can change or withdraw this anytime.
          </p>
          <div>
            <ConsentToggleRow testId="consent-hand-holding" label="Hand holding" checked={consent.hand_holding} onChange={(v) => setConsent({ ...consent, hand_holding: v })} />
            <ConsentToggleRow testId="consent-hugging" label="Hugging" checked={consent.hugging} onChange={(v) => setConsent({ ...consent, hugging: v })} />
            <ConsentToggleRow testId="consent-sitting-close" label="Sitting close" checked={consent.sitting_close} onChange={(v) => setConsent({ ...consent, sitting_close: v })} />
            <ConsentToggleRow testId="consent-arm-around-shoulder" label="Arm around shoulder" checked={consent.arm_around_shoulder} onChange={(v) => setConsent({ ...consent, arm_around_shoulder: v })} />
          </div>
          <DialogFooter>
            <Button data-testid="confirm-booking-button" onClick={confirmBooking} disabled={booking} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full w-full">
              {booking ? "Booking..." : "CONFIRM & REGISTER"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WaiverModal
        open={showWaiver}
        onOpenChange={setShowWaiver}
        onSigned={() => {
          setShowWaiver(false);
          confirmBooking();
        }}
      />
    </div>
  );
}
